// MODULES
import React from "react";
import PropTypes from "prop-types";

// CLASSES
import Session from "@classes/Session";
import Validator from "@classes/Validator";

// COMPONENTS
import Checkbox from "@components/Checkbox/Checkbox";
import DefaultFields from "../../../components/DefaultFields/DefaultFields";
import EditableField from "../../../components/EditableField/EditableField";
import FieldsCollection from "@components/FieldsCollection/FieldsCollection";

// HELPERS
import {createClassName} from "@helpers/utils";
import * as CobHelpers from "../../../Cob.helpers";
import fulfillmentCheck from "./CustomerInformation.fulfillment";
import Button from "../../../../../components/Button/Button";
import StackLayout from "../../../../../components/layouts/StackLayout/StackLayout";

import Select from "../../../../../components/Select/Select";
import countries from "@helpers/countries";
import api from "../../../../../helpers/api";
import ActivityIndicator from "../../../../../components/ActivityIndicator/ActivityIndicator";
import {delay, getPropertiesByPrefix, parseDraftDataPropsToFieldState} from "../../../../../helpers/utils";

const countriesOptions = countries.map(({label, code}) => ({label, value: code}));

export default class CobCustomerInformation extends React.PureComponent {
	static propTypes = {
		id: PropTypes.string.isRequired,
		title: PropTypes.string.isRequired,
		session: PropTypes.instanceOf(Session).isRequired,
		sectionNumber: PropTypes.number.isRequired,
		number: PropTypes.number.isRequired,
		prevState: PropTypes.object,
		onChange: PropTypes.func.isRequired,
	};

	static companyExclusive = false;
	static privateExclusive = false;
	static fields = _getFields();

	state = _getInitialState.call(this);

	async componentDidMount()
	{
		const {session, onParentCaseLoaded} = this.props;

		try {
			// If we want to start with a blank page, don't load case data
			if (session.context.caseId === "-" || session.context.caseId == null)
			{
				this.setState({
					isLoading: false,
					showError: false,
					isBlank: true
				}, () =>
				{
					onParentCaseLoaded([]);
				});
				return;
			}

			const caseInfoResult = await api("/case/data", {
				caseId: session.context.caseId,
				authToken: session.authToken
			});

			if (!caseInfoResult || caseInfoResult.Signed !== "1" || !caseInfoResult.CaseData)
			{
				this.setState({
					isLoading: false,
					showError: false,
					isBlank: true
				}, () =>
				{
					onParentCaseLoaded([]);
				});
				return;
			}

			caseInfoResult.CaseData = JSON.parse(caseInfoResult.CaseData);
			if (!caseInfoResult.CaseData.data)
			{
				this.setState({
					isLoading: false,
					showError: false,
					isBlank: true
				}, () =>
				{
					onParentCaseLoaded([]);
				});
				return;
			}
			const newFields = {...this.state.fields};
			const newValidFields = {...this.state.validFields};
			if (caseInfoResult.CaseData.data.account_no_kf)
			{
				newFields.customer_depot = caseInfoResult.CaseData.data.account_no_kf;
			}

			if (caseInfoResult.CaseData.data.customer_email && caseInfoResult.CaseData.data.customer_email.length > 0)
			{
				newFields.customer_email = caseInfoResult.CaseData.data.customer_email;
				newValidFields.customer_email = Validator.validateEmail(caseInfoResult.CaseData.data.customer_email);
			}

			this.setState({
				isLoading: false,
				showError: false,
				caseData: caseInfoResult.CaseData.data,
				fields: newFields,
				validFields: newValidFields
			}, () =>
			{
				onParentCaseLoaded(caseInfoResult.CaseData.data);
			});
		}
		catch (e)
		{

		}
	}

	componentDidUpdate(prevProps, prevState) {
		const {props, state} = this;
		const {id, onChange} = props;

		if (prevState.fields !== state.fields) {
			onChange(id, {...state}, fulfillmentCheck(props, state));
		}

		if (prevProps.draftData !== props.draftData)
		{
			if (props.draftData)
			{
				// get the draftData props specific to this section only
				const draftDataProps = getPropertiesByPrefix(props.draftData, ["customer_", "contact_"]);
				// parse the draftData props into state.fields
				const newFieldsState = parseDraftDataPropsToFieldState(this.state.fields, draftDataProps);

				this.setState({fields: {...newFieldsState}},
					async () =>
					{
						await delay(200);
						onChange(id, {...this.state}, fulfillmentCheck(this.props, this.state));
					}
				);
			}
		}
	}

	render() {
		const {props, state} = this;
		const {title, session, partner} = props;

		const classes = createClassName("Cob-CustomerInformation", {
			"Cob-View": true,
			"fulfilled": state.isFulfilled,
		});

		let ssnLong = state.fields.customer_social_security_number.replaceAll("-", "");
		let ssnShort = ssnLong;
		if (ssnLong.length > 10)
		{
			ssnShort = ssnShort.substring(2, ssnShort.length);
		}

		let selectedCitizenshipIndex = -1;
		for (let i = 0; i < countriesOptions.length; i++) {
			if (state.fields.customer_citizenship === countriesOptions[i].value) {
				selectedCitizenshipIndex = i;
				break;
			}
		}

		const signatories = [];

		for (let i = 0; i < state.fields.numberOfSignatories; i++) {
			signatories.push(
				<FieldsCollection
					fieldsPerRow={3}
					key={i}
					fields={[
						{
							render: (
								<EditableField
									name={"customer_signatory_firstname_" + i}
									label="Förnamn"
									value={state.fields["customer_signatory_firstname_" + i]}
									valid={state.fields["customer_signatory_firstname_" + i].length === 0 ? null : true}
									onChange={this._onFieldChange}
									onBlur={this._onFieldBlur}
									style={{marginBottom: "10px"}}
									required
								/>
							),
						},
						{
							render: (
								<EditableField
									name={"customer_signatory_lastname_" + i}
									label="Efternamn"
									value={state.fields["customer_signatory_lastname_" + i]}
									valid={state.fields["customer_signatory_lastname_" + i].length === 0 ? null : true}
									onChange={this._onFieldChange}
									onBlur={this._onFieldBlur}
									style={{marginBottom: "10px"}}
									required
								/>
							),
						},
						{
							render: (
								<EditableField
									type="email"
									name={"customer_signatory_email_" + i}
									label="E-postadress"
									value={state.fields["customer_signatory_email_" + i]}
									valid={state.fields["customer_signatory_email_" + i].length === 0 ? null : true}
									onChange={this._onFieldChange}
									onBlur={this._onFieldBlur}
									style={{marginBottom: "10px"}}
									required
								/>
							),
						}
					]}
				/>
			);
		}

		if (state.isLoading)
		{
			return (
				<div className={classes}>
					<div className="Cob-Fields">
						<h2>{title}</h2>

						<ActivityIndicator />
					</div>
				</div>
			);
		}

		return (
			<div className={classes}>
				<DefaultFields fields={this.constructor.fields}/>

				<input
					type="hidden"
					name="checkbox_company"
					value={session.isCompany ? "yes" : "no"}
				/>

				<input type="hidden" name="house_comp_name" value={partner.companyName}/>
				<input type="hidden" name="house_address_street" value="Engelbrektsgatan 5"/>
				<input type="hidden" name="house_address_zipcode" value="114 32"/>
				<input type="hidden" name="house_address_city" value="Stockholm"/>
				<input type="hidden" name="house_comp_no" value="5568169626"/>
				<input type="hidden" name="company_name" value={partner.companyName}/>

				<div className="Cob-Fields">
					<h2>{title}</h2>

					{!session.lookupData && (
						<FieldsCollection
							fields={[
								{
									render: (
										<Checkbox
											label="Är ett bolag"
											checked={session.isCompany}
											onChange={this._onIsCompanyChange}
										/>
									),
								},
							]}
						/>
					)}

					<FieldsCollection
						fieldsPerRow={session.isCompany ? 2 : 3}
						fields={[
							{
								render: (
									<EditableField
										name="customer_social_security_number"
										label={session.isCompany ? "Organisationsnummer" : "Personnummer"}
										placeholder={session.isCompany ? "XXXXXX-YYYY" : "ÅÅÅÅÅMMDD-XXXX"}
										value={state.fields.customer_social_security_number}
										readOnly={true}
										valid={true}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								),
							},
							{
								render: (
									<EditableField
										name="customer_firstname"
										label={session.isCompany ? "Bolag" : "Förnamn"}
										value={state.fields.customer_firstname}
										valid={!!session.lookupData || state.validFields.customer_firstname}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								),
							},
							{
								render: (
									<EditableField
										name="customer_lastname"
										label="Efternamn"
										value={state.fields.customer_lastname}
										valid={!!session.lookupData || state.validFields.customer_lastname}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								),
								display: !session.isCompany,
							}
						]}
					/>

					<input type="hidden" name="customer_name" value={state.fields.customer_firstname + (session.isCompany ? "" : " " + state.fields.customer_lastname)}/>
					<input type="hidden" name="customer_autogiro_custno" value={state.fields.customer_social_security_number}/>
					<input type="hidden" name="customer_social_security_number" value={session.isCompany ? ssnShort : ssnLong}/>

					<FieldsCollection
						fieldsPerRow={2}
						fields={[{
							render: (
								<EditableField
									type="email"
									name="customer_email"
									label={session.isCompany ? "Företagets e-postadress" : "Försäkringstagarens e-postadress"}
									value={state.fields.customer_email}
									valid={state.validFields.customer_email}
									onChange={this._onFieldChange}
									onBlur={this._onFieldBlur}
									required
								/>
							)
						},
							{
								render: (
									<Select
										name={"customer_citizenship"}
										label="Medborgarskap"
										options={countriesOptions}
										selectedIndex={selectedCitizenshipIndex}
										onChange={this._onSelectChange}
										style={{maxWidth: "300px"}}
									/>
								),
								display: !session.isCompany
							}
						]}
					/>

					<FieldsCollection
						fieldsPerRow={3}
						fields={[{
							render: (
								<EditableField
									name="customer_depot"
									label={"Försäkringsdepånummer"}
									value={state.fields.customer_depot}
									valid={state.fields.customer_depot.length === 0 ? null : state.fields.customer_depot.length > 0}
									onChange={this._onFieldChange}
									onBlur={this._onFieldBlur}
									required
								/>
							)
						}]}
					/>

					{!session.isCompany && (
						<React.Fragment>
							<input type="hidden" name="futur_insured_ssn"
								   value={state.fields.customer_social_security_number}/>
							<input type="hidden" name="futur_insured_email" value={state.fields.customer_email}/>
							<input type="hidden" name="futur_insured_citizenship"
								   value={state.fields.customer_citizenship}/>
							<input type="hidden" name="checkbox_placement_poa" value={"false"}/>
						</React.Fragment>
					)}

					{session.isCompany && (
						<React.Fragment>
							<h4 style={{margin: "40px 0 0 0"}}>Kontaktuppgifter firmatecknare</h4>

							<p style={{marginTop: "5px"}}>
								Firmatecknare måste signera avtalet, vänligen ange deras kontaktuppgifter.
							</p>

							{signatories}

							<StackLayout style={{width: "60%"}}>
								<Button
									size="small"
									label="Ta bort sista"
									appearance={signatories.length <= 1 ? "medium" : "danger"}
									disabled={signatories.length <= 1}
									onClick={() => CobHelpers.onViewFieldChange.call(this, "numberOfSignatories", state.fields.numberOfSignatories - 1)}
								/>

								<Button
									size="small"
									label="Lägg till firmatecknare"
									appearance={signatories.length >= 5 ? "medium" : "primary"}
									disabled={signatories.length >= 5}
									onClick={() => CobHelpers.onViewFieldChange.call(this, "numberOfSignatories", state.fields.numberOfSignatories + 1)}
									filled
								/>
							</StackLayout>
						</React.Fragment>
					)}
				</div>
			</div>
		);
	}

	// Internal methods
	_fieldValidator = (name, value) => {
		if (name === "customer_social_security_number") {
			return this.props.session.isCompany ? Validator.validateOrgNum(value, true) : Validator.validateSsn(value, true);
		}
		else if (name === "customer_email" || name === "contact_email") {
			return Validator.validateEmail(value);
		}
		else {
			return value.length > 0;
		}
	};

	_onFieldChange = (name, value) => {
		CobHelpers.onViewFieldChange.call(this, name, value);
	};

	_onFieldBlur = (name) => {
		CobHelpers.onViewFieldBlur.call(this, name);
	};

	_onSelectChange = (option, index) => {
		CobHelpers.onViewFieldChange.call(this, "customer_citizenship", option.value);
	};

	_onCheckboxChange = (name, checked) => {
		this.setState({
			fields: {
				...this.state.fields,
				[name]: {...this.state.fields[name], checked}
			}
		}, () => {
			const documentsRequired = [];

			if (this.state.fields.checkbox_idcheck_passport.checked) {
				documentsRequired.push("Kopia på pass");
			}

			if (this.state.fields.checkbox_idcheck_drivinglic.checked) {
				documentsRequired.push("Kopia på körkort");
			}

			if (this.state.fields.checkbox_idcheck_id.checked) {
				documentsRequired.push("Kopia på ID-kort");
			}

			if (this.state.fields.ubo_checkbox_idcheck_id.checked) {
				documentsRequired.push("Kopia på ID-kort (verklig huvudman)");
			}

			if (this.state.fields.ubo_checkbox_idcheck_drivinglic.checked) {
				documentsRequired.push("Kopia på körkort (verklig huvudman)");
			}

			if (this.state.fields.ubo_checkbox_idcheck_passport.checked) {
				documentsRequired.push("Kopia på pass (verklig huvudman)");
			}

			this.props.actions.setAppState({
				documentsRequired: documentsRequired
			});
		});
	};

	_onIsCompanyChange = (checked) => {
		const {session, onChange} = this.props;

		session.isCompany = checked;
		if (this.props.session.isCompany !== checked) {
			this.setState(_getInitialState.call(this), () => {
				onChange({...this.state}, false, this.props.number);
			});
		}
	};

	_onOptionsListChange = ({type, name}, option, index) => {
		const newOptions = [...this.state.fields[name].options].map((o, i) => {
			if (i === index) {
				return option;
			}
			else {
				return {...o, checked: type === "radio" ? false : o.checked};
			}
		});

		this.setState({
			fields: {
				...this.state.fields,
				[name]: {
					options: newOptions,
				},
			},
		}, () => {
			fulfillmentCheck(this.props, this.state);

			const documentsRequired = [];

			if (this.state.fields.kyc_documentation.options[1].checked) {
				if (this.state.fields.checkbox_idcheck_passport.checked) {
					documentsRequired.push("Kopia på pass");
				}

				if (this.state.fields.checkbox_idcheck_drivinglic.checked) {
					documentsRequired.push("Kopia på körkort");
				}

				if (this.state.fields.checkbox_idcheck_id.checked) {
					documentsRequired.push("Kopia på ID-kort");
				}

				if (this.state.fields.ubo_checkbox_idcheck_id.checked) {
					documentsRequired.push("Kopia på ID-kort (verklig huvudman)");
				}

				if (this.state.fields.ubo_checkbox_idcheck_drivinglic.checked) {
					documentsRequired.push("Kopia på körkort (verklig huvudman)");
				}

				if (this.state.fields.ubo_checkbox_idcheck_passport.checked) {
					documentsRequired.push("Kopia på pass (verklig huvudman)");
				}
			}

			this.props.actions.setAppState({
				documentsRequired: documentsRequired
			});
		});
	};
}


// PRIVATE FUNCTIONS
function _getFields() {
	return [
		"customer_firstname",
		"customer_lastname",
		"customer_social_security_number",
		"customer_email",
		"customer_citizenship",
		"customer_signatory_email_0",
		"customer_signatory_email_1",
		"customer_signatory_email_2",
		"customer_signatory_email_3",
		"customer_signatory_email_4",
		"kyc_date",
		"checkbox_customer_extcheck",
		"checkbox_customer_countercheck",

		"contact_firstname",
		"contact_lastname",
		"contact_email",
		"customer_phonenumber_home",
		"customer_phonenumber_cell",
	];
}

function _getInitialState() {
	if (this.props.prevState) {
		return {...this.props.prevState};
	}

	const {session} = this.props;
	const {isCompany} = session;
	const getLookupData = (name) => session.lookupData ? session.lookupData[name] || "" : "";

	return {
		isLoading: true,
		company: isCompany,
		validFields: !session.lookupData ? {} : {
			"customer_firstname": !!getLookupData(isCompany ? "companyName" : "firstName"),
			"customer_social_security_number": !!getLookupData(isCompany ? "orgNumber" : "ssn"),
			...(
				!isCompany ? {
					"customer_lastname": !!getLookupData("lastName")
				} : undefined
			),
		},
		fields: {
			"numberOfSignatories": 1,
			"customer_firstname": getLookupData(isCompany ? "companyName" : "firstName"),
			"customer_lastname": !isCompany ? getLookupData("lastName") : "",
			"customer_social_security_number": getLookupData(isCompany ? "orgNumber" : "ssn"),
			"customer_email": "",
			"customer_citizenship": "SE",
			"customer_signatory_firstname_0": "",
			"customer_signatory_firstname_1": "",
			"customer_signatory_firstname_2": "",
			"customer_signatory_firstname_3": "",
			"customer_signatory_firstname_4": "",
			"customer_signatory_lastname_0": "",
			"customer_signatory_lastname_1": "",
			"customer_signatory_lastname_2": "",
			"customer_signatory_lastname_3": "",
			"customer_signatory_lastname_4": "",
			"customer_signatory_email_0": "",
			"customer_signatory_email_1": "",
			"customer_signatory_email_2": "",
			"customer_signatory_email_3": "",
			"customer_signatory_email_4": "",
			"kyc_date": "",
			"customer_depot": "",

			"customer_address_street": getLookupData("street"),
			"customer_address_zipcode": getLookupData("zipCode"),
			"customer_address_city": getLookupData("city"),

			"contact_firstname": "",
			"contact_lastname": "",
			"contact_email": "",
			"customer_phonenumber_home": "",
			"customer_phonenumber_cell": "",
		},
	};
}


// WEBPACK FOOTER //
// ./src/routes/Cob/views/CustomerInformation/futur/CustomerInformation.js